import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRightLeft, Package, Trash2 } from 'lucide-react';
import { api, LitSquadDefinitionDetail, LitSquadInventoryItemDetail } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';

import { TransferItemDialog } from '../components/TransferItemDialog';
import { isSafeImageUri, resolveLitSquadItemDisplayName } from '../utils';

export function ItemDetailPage() {
  const { itemId } = useParams<{ itemId: string }>();
  const navigate = useNavigate();
  const [item, setItem] = useState<LitSquadInventoryItemDetail | null>(null);
  const [definition, setDefinition] = useState<LitSquadDefinitionDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showTransfer, setShowTransfer] = useState(false);

  useEffect(() => {
    if (!itemId) return;
    let cancelled = false;

    async function fetchData() {
      try {
        setLoading(true);
        setError(null);
        const itemData = await api.getLitSquadInventoryItem(itemId!);
        if (cancelled) return;
        setItem(itemData);

        const defData = await api.getLitSquadDefinition(itemData.itemDefId);
        if (cancelled) return;
        setDefinition(defData);
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to load LitSquad NFT');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchData();
    return () => {
      cancelled = true;
    };
  }, [itemId]);

  const handleTransfer = useCallback(
    async (toUsername: string) => {
      if (!itemId) return;
      await api.transferLitSquadItem({ toUsername, itemId });
      navigate('/a/arche_litsquad');
    },
    [itemId, navigate],
  );

  const handleBurn = useCallback(async () => {
    if (!itemId) return;
    if (!confirm('Are you sure you want to burn this LitSquad NFT? This cannot be undone.')) {
      return;
    }
    try {
      await api.burnLitSquadItem(itemId);
      navigate('/a/arche_litsquad');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to burn NFT');
    }
  }, [itemId, navigate]);

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto">
        <Skeleton className="h-8 w-48 mb-4" />
        <Skeleton className="h-64 w-full mb-4" />
        <Skeleton className="h-4 w-full mb-2" />
        <Skeleton className="h-4 w-3/4" />
      </div>
    );
  }

  if (error || !item) {
    return (
      <div className="max-w-2xl mx-auto text-center py-12">
        <p className="text-destructive">{error || 'LitSquad NFT not found'}</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate('/a/arche_litsquad')}>
          Back to Inventory
        </Button>
      </div>
    );
  }

  const displayName = resolveLitSquadItemDisplayName(item, definition?.name ?? null);
  const thumbnail = item.thumbnailUri ?? definition?.thumbnailUri ?? null;

  return (
    <div className="max-w-2xl mx-auto">
      <Button
        variant="ghost"
        size="sm"
        className="mb-4"
        onClick={() => navigate('/a/arche_litsquad')}
      >
        <ArrowLeft className="h-4 w-4 mr-1" />
        Back
      </Button>

      <div className="mb-6">
        {isSafeImageUri(thumbnail) ? (
          <img
            src={thumbnail!}
            alt={displayName}
            className="w-full max-h-96 object-contain rounded-lg bg-muted"
          />
        ) : (
          <div className="w-full h-48 bg-muted rounded-lg flex items-center justify-center">
            <Package className="h-16 w-16 text-muted-foreground" />
          </div>
        )}
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-start justify-between">
            <div>
              <CardTitle>{displayName}</CardTitle>
              <p className="text-sm text-muted-foreground mt-1">
                {definition?.name ?? 'Unknown species'} · Instance #{item.instanceNumber}
              </p>
            </div>
            <Badge>{item.status}</Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {definition?.description && (
            <p className="text-sm text-muted-foreground">{definition.description}</p>
          )}

          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">Item ID</span>
              <p className="font-mono text-xs truncate">{item.itemId}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Acquired</span>
              <p>{new Date(item.acquiredAt).toLocaleDateString()}</p>
            </div>
          </div>

          {Object.keys(item.metadata).length > 0 && (
            <div>
              <span className="text-sm text-muted-foreground">Stats</span>
              <pre className="text-xs bg-muted p-2 rounded mt-1 overflow-auto max-h-48">
                {JSON.stringify(item.metadata, null, 2)}
              </pre>
            </div>
          )}

          <div className="flex gap-2 pt-2">
            <Button variant="outline" className="flex-1" onClick={() => setShowTransfer(true)}>
              <ArrowRightLeft className="h-4 w-4 mr-2" />
              Transfer
            </Button>
            <Button variant="destructive" onClick={handleBurn}>
              <Trash2 className="h-4 w-4 mr-2" />
              Burn
            </Button>
          </div>
        </CardContent>
      </Card>

      <TransferItemDialog
        item={
          definition
            ? {
                itemDefId: item.itemDefId,
                speciesName: definition.name,
                description: definition.description,
                fungibility: 'non_fungible',
                definitionThumbnailUri: definition.thumbnailUri,
                definitionImageUri: definition.imageUri,
                definitionMetadata: definition.metadata,
                category: item.category,
                position: item.position,
                itemId: item.itemId,
                instanceNumber: item.instanceNumber,
                displayName: item.displayName,
                thumbnailUri: item.thumbnailUri,
                itemMetadata: item.metadata,
                acquiredAt: item.acquiredAt,
              }
            : null
        }
        open={showTransfer}
        onClose={() => setShowTransfer(false)}
        onConfirm={handleTransfer}
      />
    </div>
  );
}
